import React, {Component, ReactNode} from 'react'
import FacilityForReservation from '../../../facilities/FacilityForReservation'
import {AdminReserveLocationState, ReservationInfo} from '../AdminReserveInterfaces'
import {PaymentInformation, PaymentItem} from '../../ReserveFacilityModel'
import {CASH_PAYMENT_STATUS, PAYMENT_TYPE} from '../../../../CodeMaster'
import {Box, Table, TableBody, TableCell, TableRow} from '@mui/material'
import {InnerMiniTableBody, StyledTableCaption} from '../../../../styledComponents/adminPage/Table'
import {formatNumberWithCommas} from '../../../../formatters/formatNumberWithCommas'
import {StyledLink} from '../../../../styledComponents/adminPage/Links'
import PPRadioGroup from '../../../shared/forms/PPRadioGroup'
import {createPaymentViewData, PaymentViewData} from '../AdminReserveHelper'
import {Nullable} from '../../../../app/TypeHelpers'
import {BoldSpan} from '../../../../styledComponents/StyledSpan'
import {
    LeftMarginBoldSpan,
    NoMarginInnerTable,
    NoPLMinimumWidthTableCell,
    TextAlignMinimumWidthTableCell,
} from '../AdminReserveStyledComponent'
import {ReservationClass} from '../../ReservationInterfaces'
import {FixedWidthTableRowName} from './ARDetailsView'
import {NavigateFunction} from 'react-router-dom'

interface CommonProps {
    facilityData: FacilityForReservation
    reservationInfo: ReservationInfo
    paymentInfo: PaymentInformation
}

interface PropsForConfirm extends CommonProps {
    type: 'confirm'
    condominiumCd: string
    navigate: NavigateFunction
    locationState: AdminReserveLocationState
    onChange: (cashPaymentStatus: CASH_PAYMENT_STATUS) => void
}

interface PropsForComplete extends CommonProps {
    type: 'complete'
    cashPaymentStatus: Nullable<CASH_PAYMENT_STATUS>
}

type Props = PropsForConfirm | PropsForComplete

interface State {
    cashPaymentStatus: CASH_PAYMENT_STATUS
    isCashBreakdown: boolean
    isTotalBreakdown: boolean
}

class ARPaymentView extends Component<Props, State> {
    private cashTotalCharge: number
    private cashPaymentTableData: PaymentViewData[]
    private totalPaymentTableData: PaymentViewData[]

    constructor(props: Readonly<Props>) {
        super(props)

        const cashPaymentInfos: PaymentItem[] = props.reservationInfo.classification === ReservationClass.AGENT
            ? this.props.paymentInfo.items.filter(info => info.paymentType === PAYMENT_TYPE.CASH)
            : []

        this.cashTotalCharge = cashPaymentInfos
            .map(paymentInfo => paymentInfo.charge || 0)
            .reduce((accumulator, charge) => accumulator + charge, 0)

        this.cashPaymentTableData = createPaymentViewData(
            cashPaymentInfos,
            this.props.reservationInfo,
        )

        this.totalPaymentTableData = createPaymentViewData(
            this.props.paymentInfo.items,
            this.props.reservationInfo,
        )

        this.state = {
            cashPaymentStatus: CASH_PAYMENT_STATUS.YET_PAID,
            isCashBreakdown: false,
            isTotalBreakdown: false,
        }

        this.handleClickOpenCashBreakdown = this.handleClickOpenCashBreakdown.bind(this)
        this.handleClickCloseCashBreakdown = this.handleClickCloseCashBreakdown.bind(this)
        this.handleClickOpenTotalBreakdown = this.handleClickOpenTotalBreakdown.bind(this)
        this.handleClickCloseTotalBreakdown = this.handleClickCloseTotalBreakdown.bind(this)
        this.handleChangeCashPaymentStatus = this.handleChangeCashPaymentStatus.bind(this)
    }

    handleChangeCashPaymentStatus(event: React.ChangeEvent<HTMLInputElement>): void {
        const cashPaymentStatus = CASH_PAYMENT_STATUS.codeToEnumConstants(event.target.value)
        this.setState({cashPaymentStatus}, () => {
            if (this.props.type === 'confirm') {
                this.props.onChange(cashPaymentStatus)
            }
        })
    }

    handleClickOpenCashBreakdown(): void {
        this.setState({isCashBreakdown: true})
    }

    handleClickCloseCashBreakdown(): void {
        this.setState({isCashBreakdown: false})
    }

    handleClickOpenTotalBreakdown(): void {
        this.setState({isTotalBreakdown: true})
    }

    handleClickCloseTotalBreakdown(): void {
        this.setState({isTotalBreakdown: false})
    }

    renderInnerMiniTable(tableDataList: PaymentViewData[]): ReactNode {
        return (<NoMarginInnerTable>
            <InnerMiniTableBody>
                {
                    tableDataList.map((data, index) => (
                        <TableRow key={index}>
                            <NoPLMinimumWidthTableCell>
                                {data.quantityWithUnit ? `${data.article}：${data.quantityWithUnit}` : data.article}
                            </NoPLMinimumWidthTableCell>
                            <NoPLMinimumWidthTableCell align={'right'}>
                                {data.charge}
                            </NoPLMinimumWidthTableCell>
                            <NoPLMinimumWidthTableCell>
                                {data.paymentType}
                            </NoPLMinimumWidthTableCell>
                            <TableCell/>
                        </TableRow>
                    ))
                }
            </InnerMiniTableBody>
        </NoMarginInnerTable>)
    }

    render(): ReactNode {
        const hasCashPayment = (this.cashPaymentTableData.length > 0)
        if (this.props.type === 'complete' && hasCashPayment && !this.props.cashPaymentStatus) {
            throw Error('現金支払いに対するステータスがありません。')
        }

        return (<Table>
            <StyledTableCaption>合計</StyledTableCaption>
            <TableBody>
                {
                    this.cashPaymentTableData.length > 0 &&
                    <TableRow>
                        <FixedWidthTableRowName>現金支払金額</FixedWidthTableRowName>
                        <TextAlignMinimumWidthTableCell>
                            <BoldSpan>
                                {formatNumberWithCommas(this.cashTotalCharge)}円
                            </BoldSpan>
                        </TextAlignMinimumWidthTableCell>
                        <TableCell>
                            {
                                this.props.type === 'confirm'
                                    ? <label>
                                        現金支払いステータス
                                        <PPRadioGroup
                                            row
                                            data={CASH_PAYMENT_STATUS.values()}
                                            value={this.state.cashPaymentStatus.value}
                                            onChange={this.handleChangeCashPaymentStatus}
                                        />
                                    </label>
                                    : this.props.cashPaymentStatus ?
                                    <Box mb={'8px'}>
                                        現金支払いステータス
                                        <LeftMarginBoldSpan>
                                            {this.props.cashPaymentStatus.label}
                                        </LeftMarginBoldSpan>
                                    </Box>
                                    : ''
                            }
                            {
                                this.state.isCashBreakdown
                                    ? (<>
                                        <NoMarginInnerTable>
                                            <InnerMiniTableBody>
                                                {
                                                    this.cashPaymentTableData.map((data, index) => (
                                                        <TableRow key={index}>
                                                            <NoPLMinimumWidthTableCell>
                                                                {data.quantityWithUnit ? `${data.article}：${data.quantityWithUnit}` : data.article}
                                                            </NoPLMinimumWidthTableCell>
                                                            <NoPLMinimumWidthTableCell align={'right'}>
                                                                {data.charge}
                                                            </NoPLMinimumWidthTableCell>
                                                            <TableCell/>
                                                        </TableRow>
                                                    ))
                                                }
                                            </InnerMiniTableBody>
                                        </NoMarginInnerTable>
                                        <StyledLink onClick={this.handleClickCloseCashBreakdown}>内訳を閉じる</StyledLink>
                                    </>)
                                    : <StyledLink onClick={this.handleClickOpenCashBreakdown}>内訳を見る</StyledLink>
                            }
                        </TableCell>
                    </TableRow>
                }
                <TableRow>
                    <FixedWidthTableRowName>合計金額</FixedWidthTableRowName>
                    <TextAlignMinimumWidthTableCell>
                        <BoldSpan>
                            {
                                this.props.reservationInfo.classification === ReservationClass.AGENT
                                    ? formatNumberWithCommas(this.props.paymentInfo.totalCharge || 0)
                                    : 0
                            }円
                        </BoldSpan>
                    </TextAlignMinimumWidthTableCell>
                    <TableCell>
                        {
                            (this.props.reservationInfo.classification === ReservationClass.AGENT) &&
                            (this.state.isTotalBreakdown
                                    ? (<>
                                        <NoMarginInnerTable>
                                            <InnerMiniTableBody>
                                                {
                                                    this.totalPaymentTableData.map((data, index) => (
                                                        <TableRow key={index}>
                                                            <NoPLMinimumWidthTableCell>
                                                                {data.quantityWithUnit ? `${data.article}：${data.quantityWithUnit}` : data.article}
                                                            </NoPLMinimumWidthTableCell>
                                                            <NoPLMinimumWidthTableCell align={'right'}>
                                                                {data.charge}
                                                            </NoPLMinimumWidthTableCell>
                                                            <NoPLMinimumWidthTableCell>
                                                                {data.paymentType}
                                                            </NoPLMinimumWidthTableCell>
                                                            <TableCell/>
                                                        </TableRow>
                                                    ))
                                                }
                                            </InnerMiniTableBody>
                                        </NoMarginInnerTable>
                                        <StyledLink onClick={this.handleClickCloseTotalBreakdown}>内訳を閉じる</StyledLink>
                                    </>)
                                    : <StyledLink onClick={this.handleClickOpenTotalBreakdown}>内訳を見る</StyledLink>
                            )
                        }
                    </TableCell>
                </TableRow>
            </TableBody>
        </Table>)
    }
}

export default ARPaymentView
